import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/utils'
import type { KybStatus } from '@/types/kyb'
import type { OrgInvitationStatus, OrgMembership, OrgRole } from '@/types/organization'

/**
 * Shared Figma tokens and small presentational pieces for the Organization tab.
 *
 * Kept in one place so the members list, invitations list and inquiries panel
 * cannot drift apart, and so the desktop/mobile splits differ only where the JSX
 * genuinely differs rather than in styling.
 */

/** The card shell used across Profile & Settings. */
export const ORG_CARD =
  'border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]'

/** Card padding — desktop vs mobile is a pure spacing difference, so it is CSS only. */
export const ORG_CARD_PADDING_DESKTOP = 'px-8 py-8'
export const ORG_CARD_PADDING_MOBILE = 'px-3 py-3'

export const ORG_HEADING =
  'font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]'

export const ORG_SUBHEADING =
  'font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]'

export const ORG_LABEL =
  'font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'

export const ORG_TH =
  'px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'

export const ORG_TD =
  'h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:border-[#0a0a0a] dark:text-[#fdfdfd]'

/** Inner surface: rows, tiles, nested panels. */
export const ORG_SURFACE =
  'border border-[rgba(45,41,35,0.12)] bg-white dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]'

/**
 * Radix Select is styled light-only in `ui/select.tsx`; these add the dark side.
 * Colour only — callers set their own size so nothing has to rely on class-order
 * or `!important` to win a conflict.
 */
export const ORG_SELECT_TRIGGER =
  '!border-[rgba(45,41,35,0.12)] dark:!border-[rgba(253,253,253,0.12)] dark:!bg-[#1c1c1c] dark:!text-[#fdfdfd]'
/** Compact trigger for inside a table row. */
export const ORG_SELECT_TRIGGER_SM = '!h-9 !w-[132px]'
export const ORG_SELECT_CONTENT =
  'dark:!border-[rgba(253,253,253,0.12)] dark:!bg-[#1c1c1c] dark:!text-[#fdfdfd]'
export const ORG_SELECT_ITEM = 'dark:focus:!bg-[rgba(253,253,253,0.08)]'

/** Bordered danger action, matching the Delete button in `InquiriesTable`. */
export const ORG_DANGER_BUTTON =
  'inline-flex items-center justify-center gap-1 border border-[#ff540c] bg-[rgba(255,255,255,0.72)] px-4 py-[10px] font-plus-jakarta text-[14px] font-medium uppercase leading-[20px] text-[#ff540c] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[rgba(10,10,10,0.72)]'

/** Bordered neutral action, same geometry as the danger button. */
export const ORG_GHOST_BUTTON =
  'inline-flex items-center justify-center gap-1 border border-[rgba(45,41,35,0.24)] bg-[rgba(255,255,255,0.72)] px-4 py-[10px] font-plus-jakarta text-[14px] font-medium uppercase leading-[20px] text-[#2d2923] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 dark:border-[rgba(253,253,253,0.24)] dark:bg-[rgba(10,10,10,0.72)] dark:text-[#fdfdfd]'

const BADGE_BASE =
  'inline-flex items-center gap-1 border px-[10px] py-[6px] font-plus-jakarta text-[12px] font-medium leading-[16px]'

const ROLE_BADGE: Record<OrgRole, string> = {
  OWNER: 'border-[#1fb845] bg-[rgba(31,184,69,0.08)] text-[#188f36]',
  ADMIN: 'border-[#3b82f6] bg-[rgba(59,130,246,0.08)] text-[#2563eb]',
  MEMBER:
    'border-[rgba(45,41,35,0.24)] bg-[rgba(45,41,35,0.06)] text-[rgba(45,41,35,0.72)] dark:border-[rgba(253,253,253,0.24)] dark:bg-[rgba(253,253,253,0.06)] dark:text-[rgba(253,253,253,0.72)]',
}

const ROLE_LABEL: Record<OrgRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
}

export const RoleBadge: React.FC<{ role: OrgRole; className?: string }> = ({ role, className }) => (
  <span className={cn(BADGE_BASE, ROLE_BADGE[role], className)}>{ROLE_LABEL[role]}</span>
)

const INVITE_BADGE: Record<OrgInvitationStatus, { label: string; className: string }> = {
  PENDING: {
    label: 'Pending',
    className: 'border-[#ff8e00] bg-[rgba(255,142,0,0.06)] text-[#c26c00]',
  },
  ACCEPTED: {
    label: 'Accepted',
    className: 'border-[#1fb845] bg-[rgba(31,184,69,0.08)] text-[#188f36]',
  },
  REVOKED: {
    label: 'Revoked',
    className: 'border-[#ff540c] bg-[rgba(255,84,12,0.08)] text-[#d74404]',
  },
  EXPIRED: {
    label: 'Expired',
    className:
      'border-[rgba(45,41,35,0.24)] bg-[rgba(45,41,35,0.06)] text-[rgba(45,41,35,0.72)] dark:border-[rgba(253,253,253,0.24)] dark:bg-[rgba(253,253,253,0.06)] dark:text-[rgba(253,253,253,0.72)]',
  },
}

export const InvitationStatusBadge: React.FC<{ status: OrgInvitationStatus }> = ({ status }) => {
  const meta = INVITE_BADGE[status] ?? INVITE_BADGE.PENDING
  return <span className={cn(BADGE_BASE, meta.className)}>{meta.label}</span>
}

/**
 * The three states an organization's email-domain claim can be in.
 *
 * `unverified` and `verified` are deliberately kept apart: a claim is just a
 * string an admin typed, and only a redeemed challenge to a mailbox at that
 * domain proves control of it. The badge must never let the two read alike.
 */
export type EmailDomainState = 'none' | 'unverified' | 'verified'

/** Which state an organization's domain fields describe. */
export const emailDomainState = (org: {
  email_domain?: string | null
  email_domain_verified?: boolean
}): EmailDomainState => {
  if (!org.email_domain) return 'none'
  return org.email_domain_verified ? 'verified' : 'unverified'
}

const DOMAIN_BADGE: Record<EmailDomainState, { label: string; className: string }> = {
  none: {
    label: 'Not claimed',
    className:
      'border-[rgba(45,41,35,0.24)] bg-[rgba(45,41,35,0.06)] text-[rgba(45,41,35,0.72)] dark:border-[rgba(253,253,253,0.24)] dark:bg-[rgba(253,253,253,0.06)] dark:text-[rgba(253,253,253,0.72)]',
  },
  unverified: {
    label: 'Unverified',
    className: 'border-[#ff8e00] bg-[rgba(255,142,0,0.06)] text-[#c26c00] dark:text-[#ffa733]',
  },
  verified: {
    label: 'Verified',
    className: 'border-[#1fb845] bg-[rgba(31,184,69,0.08)] text-[#188f36] dark:text-[#3fd465]',
  },
}

export const DomainStatusBadge: React.FC<{ state: EmailDomainState; className?: string }> = ({
  state,
  className,
}) => (
  <span className={cn(BADGE_BASE, DOMAIN_BADGE[state].className, className)}>
    {DOMAIN_BADGE[state].label}
  </span>
)

/**
 * The six states an organization's KYB (business verification) can be in.
 *
 * Same rule as the email-domain badge above, and it matters more here: these
 * are never collapsed into "done / not done". A review that is still open has
 * proved nothing, a `retry` is recoverable while a `rejected` is not, and a
 * verification nobody has started is not a verification that failed. Each state
 * carries its own label, its own colour and — at the call site — its own icon
 * and its own sentence, so no two of them can read alike.
 *
 * Mirrors `KybStatus` in `@/types/kyb` plus `none` for "no verification row".
 */
export type KybState = 'none' | 'in_progress' | 'pending_review' | 'retry' | 'approved' | 'rejected'

/**
 * Which state a `GET /organizations/{id}/kyb` answer describes.
 *
 * Deliberately an exhaustive switch and not `status ?? 'none'`: the argument is
 * typed, but the value comes off the wire, so a status added by a newer backend
 * would otherwise flow straight into `KYB_BADGE[state]` and render an empty
 * badge with no styling at all. Anything unrecognised degrades to `none`, the
 * one state that claims nothing — an unknown status must never inherit an
 * affirmative treatment.
 */
export const kybState = (status: KybStatus | null | undefined): KybState => {
  switch (status) {
    case 'in_progress':
    case 'pending_review':
    case 'retry':
    case 'approved':
    case 'rejected':
      return status
    default:
      return 'none'
  }
}

const KYB_BADGE: Record<KybState, { label: string; className: string }> = {
  none: {
    label: 'Not started',
    className:
      'border-[rgba(45,41,35,0.24)] bg-[rgba(45,41,35,0.06)] text-[rgba(45,41,35,0.72)] dark:border-[rgba(253,253,253,0.24)] dark:bg-[rgba(253,253,253,0.06)] dark:text-[rgba(253,253,253,0.72)]',
  },
  in_progress: {
    label: 'In progress',
    className: 'border-[#ff8e00] bg-[rgba(255,142,0,0.06)] text-[#c26c00] dark:text-[#ffa733]',
  },
  // Blue, not amber and certainly not green: an open review is neither the
  // user's move nor a result.
  pending_review: {
    label: 'Under review',
    className: 'border-[#3b82f6] bg-[rgba(59,130,246,0.08)] text-[#2563eb] dark:text-[#7aa7f7]',
  },
  retry: {
    label: 'Action needed',
    className: 'border-[#ff540c] bg-[rgba(255,84,12,0.08)] text-[#d74404] dark:text-[#ff8a5c]',
  },
  approved: {
    label: 'Verified',
    className: 'border-[#1fb845] bg-[rgba(31,184,69,0.08)] text-[#188f36] dark:text-[#3fd465]',
  },
  // The only FILLED badge on the tab. `retry` and `rejected` are both red-ish
  // by nature, so the terminal one is given a different weight rather than a
  // different shade — a reader must not have to compare two hex values to tell
  // "fix this and resubmit" from "this cannot proceed".
  rejected: {
    label: 'Rejected',
    className: 'border-[#d74404] bg-[#d74404] text-white',
  },
}

export const KybStatusBadge: React.FC<{ state: KybState; className?: string }> = ({
  state,
  className,
}) => (
  <span className={cn(BADGE_BASE, KYB_BADGE[state].className, className)}>
    {KYB_BADGE[state].label}
  </span>
)

/**
 * The review note that may be shown to the applicant — and the only one.
 *
 * 🔴 Sumsub writes TWO comments on a rejection: `moderationComment`, written
 * for the applicant, and `clientComment`, written for us. The second one can
 * name the exact check that fired and is internal; it must never reach a
 * browser. The backend already drops it (`OrgKybStatus` has no such key and
 * `kybService.getStatus` rebuilds the object from three named fields), and this
 * is the second barrier: the parameter type below admits ONLY
 * `moderation_comment`, so reading anything else here is a compile error, and
 * the card renders the string this function returns rather than reaching into
 * the status object itself.
 *
 * Returns `null` for states where a note would be meaningless (nothing has been
 * reviewed yet) or misleading (an approval does not need a caveat attached).
 */
export const kybApplicantComment = (
  status: { moderation_comment?: string | null } | null | undefined,
  state: KybState,
): string | null => {
  if (state !== 'in_progress' && state !== 'retry' && state !== 'rejected') return null
  const comment = status?.moderation_comment?.trim()
  return comment ? comment : null
}

/** "You" marker so a manager can tell their own row apart at a glance. */
export const SelfBadge: React.FC = () => (
  <span className="inline-flex items-center bg-[rgba(45,41,35,0.12)] px-1 py-[2px] font-plus-jakarta text-[12px] font-medium leading-[16px] text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
    You
  </span>
)

/** Display name for a membership, degrading to email then to the raw user id. */
export const memberName = (member: Pick<OrgMembership, 'user' | 'user_id'>): string => {
  const full = [member.user?.first_name, member.user?.last_name].filter(Boolean).join(' ')
  return full || member.user?.email || member.user_id
}

/**
 * Is this membership the signed-in user?
 *
 * `id` is optional on `AuthUser` (it is absent from some auth payloads), so we
 * fall back to a case-insensitive email match. Getting this wrong would put a
 * "Remove" button on the viewer's own row — and `DELETE /members/{user_id}` does
 * not refuse self-removal, it only refuses removing the *last* owner.
 */
export const isCurrentUserMembership = (
  member: Pick<OrgMembership, 'user' | 'user_id'>,
  currentUser: { id?: string; email?: string } | null | undefined,
): boolean => {
  if (!currentUser) return false
  if (currentUser.id && member.user_id === currentUser.id) return true
  const memberEmail = member.user?.email?.toLowerCase()
  const currentEmail = currentUser.email?.toLowerCase()
  return !!memberEmail && !!currentEmail && memberEmail === currentEmail
}

export const memberInitials = (member: Pick<OrgMembership, 'user' | 'user_id'>): string => {
  const first = member.user?.first_name?.[0] ?? ''
  const last = member.user?.last_name?.[0] ?? ''
  const initials = (first + last).trim()
  if (initials) return initials.toUpperCase()
  const source = member.user?.email || member.user_id
  return source.slice(0, 2).toUpperCase()
}

export const MemberAvatar: React.FC<{
  member: Pick<OrgMembership, 'user' | 'user_id'>
  size?: number
}> = ({ member, size = 26 }) => (
  <div
    className="flex shrink-0 items-center justify-center rounded-full bg-[rgba(45,41,35,0.12)] font-plus-jakarta text-[10px] font-semibold text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]"
    style={{ height: size, width: size }}
  >
    {memberInitials(member)}
  </div>
)

/** Centered empty state, matching the one in `InquiriesTable`. */
export const OrgEmptyState: React.FC<{
  icon: LucideIcon
  title: string
  description: string
  action?: React.ReactNode
  className?: string
}> = ({ icon: Icon, title, description, action, className }) => (
  <div className={cn('text-center', className)}>
    <Icon
      className="mx-auto mb-4 text-[rgba(45,41,35,0.4)] dark:text-[rgba(253,253,253,0.4)]"
      size={40}
    />
    <h3 className={ORG_HEADING}>{title}</h3>
    <p className={cn('mt-2', ORG_SUBHEADING)}>{description}</p>
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
)

/** Inline error banner, matching the red banner in `ProfileScreen`. */
export const OrgErrorBanner: React.FC<{ message: string; className?: string }> = ({
  message,
  className,
}) => (
  <div
    className={cn(
      'border border-red-500 bg-red-500/20 px-4 py-3 font-plus-jakarta text-[14px] leading-[20px] text-red-500 dark:text-red-400',
      className,
    )}
  >
    {message}
  </div>
)
